import { logger } from '@tuic/logger';
import { cache } from 'react';

import { Account, createRateLimitHandler } from '../../index';
import { errorContains } from '../../utils';

import { ADD_TRIPS_RATE_LIMIT_NAME, AddTripStatus } from './trips.types';

import type { AddTripResult } from './trips.types';

const getAccountTrips = async (
  idToken: string,
): Promise<Account.MyBooking2[]> => {
  const { data, error } = await Account.getMyTrips2({
    auth: idToken,
  });

  if (error) {
    logger.error(`Could not fetch trips: ID token: ${idToken}`);
    logger.error(error);

    return [];
  }

  return data ?? [];
};

/**
 * Get all accounts that are associated with the current user.
 */
export const memoizedGetAccountTrips = cache(getAccountTrips);

type AddTripHandler = (
  args: Account.MyTripRequest & {
    idToken: string;
  },
) => Promise<AddTripResult>;

/**
 * Add a trip to the user's account.
 */
export const addTrip: AddTripHandler = async args => {
  const { idToken, ...body } = args;

  const { data, response, error } = await Account.addMyTrip({
    body,
    auth: idToken,
  });

  const rateLimit = await createRateLimitHandler(
    ADD_TRIPS_RATE_LIMIT_NAME,
    response,
  );

  if (rateLimit.limited) {
    return {
      status: AddTripStatus.RATE_LIMIT,
      rateLimit,
    };
  }

  if (data?.success) {
    return {
      status: AddTripStatus.SUCCESS,
      rateLimit,
    };
  }

  if (error) {
    logger.error(
      `[addTrip] Failed adding trip. Params: ${JSON.stringify(args)}`,
    );
    logger.error(error);
  }

  if (
    errorContains(error, 'bad request parameter - bad firstName or lastName')
  ) {
    return {
      status: AddTripStatus.NAME_DOES_NOT_MATCH,
      rateLimit,
    };
  }

  if (errorContains(error, 'bad request parameter - bad bookingCode')) {
    return {
      status: AddTripStatus.BAD_BOOKING_CODE,
      rateLimit,
    };
  }

  if (errorContains(error, 'booking already added to other account')) {
    return {
      status: AddTripStatus.ALREADY_ADDED_TO_OTHER_ACCOUNT,
      rateLimit,
      maskedEmail: (error as { maskedEmail?: string })?.maskedEmail,
    };
  }

  if (errorContains(error, 'booking already added')) {
    return {
      status: AddTripStatus.ALREADY_ADDED,
      rateLimit,
    };
  }

  if (errorContains(error, 'booking in the past')) {
    return {
      status: AddTripStatus.BOOKING_IN_THE_PAST,
      rateLimit,
    };
  }

  if (errorContains(error, 'booking not found')) {
    return {
      status: AddTripStatus.BOOKING_NOT_FOUND,
      rateLimit,
    };
  }

  if (errorContains(error, 'name does not match')) {
    return {
      status: AddTripStatus.NAME_DOES_NOT_MATCH,
      rateLimit,
    };
  }

  if (errorContains(error, 'unexpected string response')) {
    return {
      status: AddTripStatus.UNEXPECTED_STRING_RESPONSE,
      rateLimit,
    };
  }

  return {
    status: AddTripStatus.GENERAL_ERROR,
    rateLimit,
  };
};
